import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { COLOR_TOKENS } from '@backrs_ui/common-ui/src/designSystem/colors/colors';
import { formatCurrency } from '@backrs_ui/common-ui/src/utils/stringFormatters.util';

import { Box, SxProps, Theme, Typography } from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Line } from 'react-chartjs-2';

import { ROILineChartHeader } from './ROILineChartHeader';
import { ROIData, roiMethodConfigFor } from './roiMethodConfig';

const ROI_LINE_CHART_STRINGS = appStrings.modules.charts.returnOnInvestment;

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  annotationPlugin,
);

type ROILineChartProps = Readonly<{
  roiData?: ROIData | null;
  title: string;
}>;

export const ROILineChart = ({ roiData, title }: ROILineChartProps) => {
  const config = roiData ? roiMethodConfigFor(roiData) : null;

  if (!roiData || !config) return null;

  const { headline, points, shadedBounds, yAxis, levelThresholds, strings } = config;
  const headlineRoi = headline(roiData);

  if (headlineRoi == null) return null;

  const dataPoints = points(roiData);
  const labels = dataPoints.map((p) => p.label);
  const values = dataPoints.map((p) => p.value);

  const data = {
    labels,
    datasets: [
      {
        label: 'upper',
        data: [...shadedBounds.upper],
        borderColor: 'transparent',
        backgroundColor: 'transparent',
        pointRadius: 0,
        pointHoverRadius: 0,
        tension: 0,
        fill: false,
        order: 2,
      },
      {
        label: 'lower',
        data: [...shadedBounds.lower],
        borderColor: 'transparent',
        backgroundColor: COLOR_TOKENS['Brand/100'],
        pointRadius: 0,
        pointHoverRadius: 0,
        tension: 0,
        fill: '-1',
        order: 2,
      },
      {
        label: title,
        data: values,
        borderColor: COLOR_TOKENS['Brand/500'],
        tension: 0,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: COLOR_TOKENS['Brand/500'],
        fill: false,
        order: 0,
      },
    ],
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        title: {
          display: true,
          text: ROI_LINE_CHART_STRINGS.yAxisLabel,
          color: COLOR_TOKENS['Colors/Text/text-tertiary'],
          font: {
            size: 12,
            family: 'Sarabun, sans-serif',
          },
        },
        grid: {
          tickColor: 'transparent',
          display: true,
          color: (context) => {
            const value = context.tick.value;

            if (value === 0) {
              return COLOR_TOKENS['Gray/300'];
            }

            return COLOR_TOKENS['Colors/Border/border-tertiary'];
          },
        },
        border: {
          display: false,
        },
        min: yAxis.min,
        max: yAxis.max,
        afterBuildTicks: (axis) => {
          axis.ticks = yAxis.ticks.map((value) => ({
            value,
          }));
        },
        ticks: {
          callback: (value) => {
            const valueAsNumber = Number(value);

            if (valueAsNumber === yAxis.min) return '';
            if (valueAsNumber === 0) return '$0 ';

            const absValue = Math.abs(valueAsNumber);
            const sign = valueAsNumber < 0 ? '-' : '';

            // format values <1M as $###k, others as $#M
            if (absValue < 1000000) {
              return `${sign}$${absValue / 1000}k `;
            }

            return `${sign}$${absValue / 1000000}M `;
          },
          font: {
            size: 12,
            weight: 'lighter',
            family: 'Sarabun, sans-serif',
          },
        },
      },
      x: {
        title: {
          display: false,
        },
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
        ticks: {
          align: 'center',
          font: {
            size: 12,
            weight: 'lighter',
            family: 'Sarabun, sans-serif',
          },
        },
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
    plugins: {
      tooltip: {
        enabled: true,
        displayColors: false,
        padding: {
          top: 8,
          bottom: 8,
          left: 12,
          right: 12,
        },
        caretSize: 0,
        yAlign: 'bottom',
        filter: function (tooltipItem) {
          return tooltipItem.dataset.label !== 'upper' && tooltipItem.dataset.label !== 'lower';
        },
        callbacks: {
          title: function (context) {
            return context[0]?.label ?? '';
          },
          label: function (context) {
            const value = context.parsed.y;

            return `ROI: ${formatCurrency(value)}`;
          },
        },
      },
      legend: {
        labels: {
          generateLabels: () => [],
        },
      },
      title: {
        display: false,
      },
    },
    hover: {
      mode: 'index',
      intersect: false,
    },
  };

  return (
    <Box sx={containerSx}>
      <ROILineChartHeader
        total_roi={headlineRoi}
        strings={strings}
        levelThresholds={levelThresholds}
      />

      <Box sx={chartContainerSx}>
        <Line data={data} options={options} />
      </Box>

      <Box sx={legendContainerSx}>
        <Box sx={legendColorBoxSx} />
        <Typography variant="text-sm-regular" sx={legendTextSx}>
          {strings.legendLabel}
        </Typography>
      </Box>
    </Box>
  );
};

const chartContainerSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  height: '100%',
  mt: backrs.spacing['spacing-xl'],
});

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  '@media (max-width: 768px)': {
    width: '100%',
  },
  '@media (max-width: 400px)': {
    width: '100%',
  },
  height: { xs: 'auto', lg: '405px' },
  backgroundColor: backrs.colors['Colors/Base/White'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-primary']}`,
  boxShadow: backrs.shadows['shadow-xs'],
  borderRadius: backrs.radius['radius-xl'],
  padding: {
    xs: backrs.spacing['spacing-xl'],
    sm: backrs.spacing['spacing-2xl'],
  },
});

const legendContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-sm'],
  mt: backrs.spacing['spacing-lg'],
});

const legendColorBoxSx: SxProps<Theme> = ({ backrs }) => ({
  width: '16px',
  height: '16px',
  backgroundColor: COLOR_TOKENS['Brand/100'],
  borderRadius: backrs.radius['radius-xs'],
  flexShrink: 0,
});

const legendTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});
